// src/utils/brouillonFnct.ts — SCE Issues
// « Garder en brouillon » une FNC-T pas encore ouverte (refonte de l'ouverture,
// étape 5 — décision 6 du 14/10, docs/chantier-fnct.md § 13). Pur, testable.
//
// Un brouillon par problème, dans le bloc `brouillonsFnct` du JSON `Prefs` de la
// ligne de la personne dans SCE_UserPrefs : il la suit d'un appareil à l'autre.
// Plus une copie locale à chaque saisie (comme utils/brouillonNcs) : rien ne se
// perd si la page se ferme avant « Garder ». Le plus récent des deux l'emporte.
// Aucune colonne. Le brouillon garde le texte et les choix ; un fichier, non.
// Oublié au bout de 30 jours.

import type { EtapeOuverture, SaisieOuverture } from './ouvertureFnct'
import { jourLisible, jourParis } from './fnct'

export const CLE_BLOC_BROUILLONS_FNCT = 'brouillonsFnct'
export const DUREE_BROUILLON_FNCT_MS = 30 * 86_400_000

/** Clé du brouillon d'une FNC-T ouverte depuis un équipement (sans problème) : `machine:4954-H`. */
export function cleBrouillonMachine(numeroSerie: string): string {
  return `machine:${numeroSerie.trim().toUpperCase()}`
}

/** « Brouillon du 20/10 · étape 2 sur 4 » (fiche problème, fiche équipement). */
export function resumeBrouillon(b: Pick<BrouillonFnct, 'le' | 'etape'>): string {
  const d = new Date(b.le)
  const jour = Number.isNaN(d.getTime()) ? '' : ` du ${jourLisible(jourParis(d)).slice(0, 5)}`
  return `Brouillon${jour} · étape ${b.etape} sur 4`
}

export interface BrouillonFnct {
  v: 1
  etape: EtapeOuverture
  saisie: SaisieOuverture
  /** Date et heure de la dernière saisie, ISO 8601. */
  le: string
}

const tableau = (x: unknown): boolean => Array.isArray(x)
const texte = (x: unknown): boolean => typeof x === 'string'

/** La saisie relue a-t-elle la forme attendue ? (un brouillon d'une autre version est écarté) */
function saisieValide(x: unknown): x is SaisieOuverture {
  if (typeof x !== 'object' || x === null) return false
  const s = x as Record<string, unknown>
  return (
    (s.cas === 'pid' || s.cas === 'general') &&
    tableau(s.retires) &&
    tableau(s.ajoutes) &&
    tableau(s.numerosSerie) &&
    ['categorie', 'description', 'documentsImpactes', 'justification', 'lieuDetection', 'priorite', 'leader', 'leaderUPN', 'expert', 'conceptionProcess'].every((k) => texte(s[k])) &&
    (s.dateDetection === null || texte(s.dateDetection)) &&
    (s.actions === null || tableau(s.actions))
  )
}

/** Un brouillon relu (bloc SharePoint ou copie locale), ou null s'il est illisible ou trop vieux. */
export function lireBrouillon(x: unknown, maintenant: Date): BrouillonFnct | null {
  if (typeof x !== 'object' || x === null) return null
  const b = x as Record<string, unknown>
  if (b.v !== 1 || !saisieValide(b.saisie) || typeof b.le !== 'string') return null
  const etape = b.etape
  if (etape !== 1 && etape !== 2 && etape !== 3 && etape !== 4) return null
  const age = maintenant.getTime() - Date.parse(b.le)
  if (Number.isNaN(age) || age > DUREE_BROUILLON_FNCT_MS) return null
  return { v: 1, etape, saisie: b.saisie, le: b.le }
}

/** Les brouillons du bloc, par ID de problème ; les illisibles et les trop vieux écartés. */
export function lireBrouillons(bloc: unknown, maintenant: Date): Record<string, BrouillonFnct> {
  if (typeof bloc !== 'object' || bloc === null || Array.isArray(bloc)) return {}
  const res: Record<string, BrouillonFnct> = {}
  for (const [id, x] of Object.entries(bloc)) {
    const b = lireBrouillon(x, maintenant)
    if (b) res[id] = b
  }
  return res
}

/** Le bloc avec ce brouillon (remplacé), purgé des trop vieux. */
export function avecBrouillon(bloc: unknown, issueId: string, b: BrouillonFnct, maintenant: Date): Record<string, BrouillonFnct> {
  return { ...lireBrouillons(bloc, maintenant), [issueId]: b }
}

/** Le bloc sans ce brouillon, purgé des trop vieux. */
export function sansBrouillon(bloc: unknown, issueId: string, maintenant: Date): Record<string, BrouillonFnct> {
  const res = lireBrouillons(bloc, maintenant)
  delete res[issueId]
  return res
}

/** Le plus récent des deux (copie locale, bloc SharePoint). */
export function plusRecent(a: BrouillonFnct | null, b: BrouillonFnct | null): BrouillonFnct | null {
  if (!a) return b
  if (!b) return a
  return a.le >= b.le ? a : b
}

// ─── Copie locale (ce navigateur) ───

const cleLocale = (issueId: string): string => `sce-issues:brouillon-fnct:${issueId}`

export function lireBrouillonLocal(issueId: string, maintenant = new Date()): BrouillonFnct | null {
  try {
    const t = localStorage.getItem(cleLocale(issueId))
    return t ? lireBrouillon(JSON.parse(t), maintenant) : null
  } catch {
    return null
  }
}

export function ecrireBrouillonLocal(issueId: string, b: BrouillonFnct): void {
  try {
    localStorage.setItem(cleLocale(issueId), JSON.stringify(b))
  } catch {
    // Stockage plein ou interdit : le brouillon SharePoint reste.
  }
}

export function effacerBrouillonLocal(issueId: string): void {
  try {
    localStorage.removeItem(cleLocale(issueId))
  } catch {
    // Rien à faire.
  }
}
